import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import type { GapsRow } from '../GovernanceAnalyticsAiVisibility';
import { FacetConfig, findConfiguredTerm } from '../GovernanceAnalyticsAiVisibility.utils';
import { bandThresholdMarkers, TONE_BAND_METRIC } from './bandVisuals.utils';
import { TONE_METRIC_LABEL } from '../toneMetric';
import { chartYDomain } from './chartYDomain.utils';

/**
 * Everything the Brand landscape card decides, with no React and no chart library in sight.
 *
 * The card is a second reader of rows the Gaps table already fetched, so every choice it makes —
 * which brands are drawable, where their tiles sit, what each tile says on hover — is a function of
 * those rows. Keeping that here is what lets the whole set of rules be asserted directly, including
 * the two the rendered DOM hides: that the layout does not depend on the order rows arrive in, and
 * that a brand missing a figure is never quietly placed at a midpoint.
 */

/**
 * A Gaps row, narrowed to the fields this card reads.
 *
 * Derived from the screen's own row rather than from the wire model: by the time a row reaches here
 * the tag key has been unwrapped out of its wrapper object and both figures have been clamped to
 * [0, 1], so the client's model no longer describes it. `count` is distinct prompts and is never
 * presented as the n behind Visibility or Sentiment; `answerCount` is the distinct answers the brand
 * was seen in.
 */
export type BrandLandscapeRow = Pick<GapsRow, 'term' | 'count' | 'visibility' | 'sentiment' | 'answerCount'>;

/**
 * The four quadrants, spelled as the product spells them.
 *
 * "Laggers" is the product's word and stays as written: the four names are display copy the reader
 * learns once, and renaming one here would leave the screen and the vocabulary disagreeing.
 */
export const BRAND_QUADRANTS = ['Niche Players', 'Leaders', 'Laggers', 'Controversial'] as const;
export type BrandQuadrant = (typeof BRAND_QUADRANTS)[number];

/**
 * What each quadrant name means, in the words the hover shows.
 *
 * Without these a reader has to guess which corner is the threat, and "Controversial" in particular
 * reads as "divisive" rather than "widely seen, poorly rated" — the opposite of a warning.
 */
export const QUADRANT_RULES: Record<BrandQuadrant, string> = {
  'Niche Players': 'Seen rarely, spoken of well',
  Leaders: 'Seen often, spoken of well',
  Laggers: 'Seen rarely, spoken of poorly',
  Controversial: 'Seen often, spoken of poorly',
};

/** Where the plot splits, on both axes. Exactly this value reads in the higher quadrant. */
export const QUADRANT_MIDPOINT = 0.5;

/** The vertical window the Sentiment axis draws, on the metric's own 0–1 scale. */
export type SentimentDomain = { min: number; max: number };

/** The whole scale, drawn when there is nothing to zoom to. */
export const FULL_SENTIMENT_DOMAIN: SentimentDomain = { min: 0, max: 1 };

/** The band boundaries the Sentiment axis is labelled by, as the whole percentages `chartYDomain` reads. */
const SENTIMENT_THRESHOLD_PERCENTS = bandThresholdMarkers(TONE_BAND_METRIC).map((marker) => marker.percent);

/**
 * The vertical window the Sentiment axis draws in, for a set of measured sentiments.
 *
 * Zoomed for the same reason every trend chart here is, and by the same rule — `chartYDomain`,
 * called rather than re-derived: sentiments cluster, and against a fixed 0–1 axis the spread
 * between brands — the thing this card exists to show — flattens into one horizontal band of tiles.
 * The window is widened to the band boundaries that bracket the data and no further, so the axis
 * always has a glyph on it saying which band the tiles sit in without buying that glyph with a
 * stretch of empty plot.
 *
 * Nothing pulls the window towards any particular value. The axis's landmarks are the band
 * boundaries of the measure the tone switch selects, not a middle to hold on screen, so a window
 * widened to reach one would be paying for a line the reader has no use for with the resolution they
 * came for.
 */
export function sentimentPlotDomain(values: readonly (number | undefined)[]): SentimentDomain {
  const measured = values
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
    .map((value) => clamp(value, 0, 1) * 100);

  if (!measured.length) {
    return FULL_SENTIMENT_DOMAIN;
  }

  const zoomed = chartYDomain(measured, SENTIMENT_THRESHOLD_PERCENTS);

  return { min: zoomed.min / 100, max: zoomed.max / 100 };
}

/**
 * Where a sentiment sits in the drawn window: 0 at the bottom edge, 1 at the top.
 *
 * The one place the window is turned into a position, read by the layout and by the axis alike, so
 * a tile can never sit somewhere the scale beside it does not point at.
 */
export function sentimentPosition(sentiment: number, domain: SentimentDomain): number {
  const span = domain.max - domain.min;
  if (!(span > 0)) {
    return QUADRANT_MIDPOINT;
  }

  return clamp((sentiment - domain.min) / span, 0, 1);
}

/** A figure a brand has no measurement for, named as the axis names it. */
export type MissingFigure = 'Visibility' | typeof TONE_METRIC_LABEL;

/** A brand the card can show — plotted when `missing` is empty, greyed otherwise. */
export type LandscapeBrand = {
  /** The taxonomy tag key. Also the layout's sort key and the domain map's key. */
  key: string;
  name: string;
  facet?: TaxonomyFacet;
  visibility?: number;
  sentiment?: number;
  answerCount?: number;
  /** The figures nobody has measured yet, Visibility before Sentiment. Empty for a plotted brand. */
  missing: MissingFigure[];
};

/** A brand with both figures, and therefore a coordinate. */
export type PlottedBrand = LandscapeBrand & { visibility: number; sentiment: number };

/** A tile's place in the plot rectangle, in pixels from its top-left corner. */
export type PlacedBrand = {
  brand: PlottedBrand;
  quadrant: BrandQuadrant;
  /** The tile's top-left corner. */
  x: number;
  y: number;
  /**
   * The walk found no free candidate, so the tile stayed on its true coordinate.
   *
   * A record of what PLACEMENT did, and not the same question as whether the reader is looking at a
   * stack: a tile left here because of a brand they have since switched off is alone on the plot.
   * `overlappingKeys` answers that one, over the tiles actually drawn.
   */
  unseparated: boolean;
};

export function isPlotted(brand: LandscapeBrand): brand is PlottedBrand {
  return !brand.missing.length;
}

/**
 * The facet a stats row's tag belongs to, or undefined when no configured facet claims it.
 *
 * Matching goes through `findConfiguredTerm` rather than repeating its key/id/name rule, so the two
 * cannot drift into disagreeing about which tag a row belongs to.
 */
export function facetOfTerm(configs: FacetConfig[], value: string): TaxonomyFacet | undefined {
  return configs.find((config) => !!findConfiguredTerm([config], value))?.facet;
}

/**
 * The brands the card shows, from the rows the Gaps table holds.
 *
 * Two kinds of row never become a brand. The unnamed bucket (`term: null`) is a real category in the
 * table below but has no identity to put a logo on. A row with no prompts and no figure at all is a
 * tag that has content and no AI measurement — the Gaps table exists to show exactly that, and this
 * card has nothing to say about it.
 *
 * `nameOf` is required rather than optional: a row carries the tag KEY and never a display name,
 * while every surface here — the chips, the hovers, the accessible label — shows the name.
 */
export function plottableBrands(
  rows: readonly BrandLandscapeRow[],
  { facetOf, nameOf }: { facetOf: (key: string) => TaxonomyFacet | undefined; nameOf: (key: string) => string }
): LandscapeBrand[] {
  const brands: LandscapeBrand[] = [];

  for (const row of rows) {
    if (row.term === null) {
      continue;
    }

    const hasVisibility = typeof row.visibility === 'number' && Number.isFinite(row.visibility);
    const hasSentiment = typeof row.sentiment === 'number' && Number.isFinite(row.sentiment);
    // Guarded as a finite number, the way the two figures above are, rather than for truthiness: a
    // count that arrived as something other than a positive number is no measurement to keep a tag on.
    const hasCount = typeof row.count === 'number' && Number.isFinite(row.count) && row.count > 0;

    if (!hasCount && !hasVisibility && !hasSentiment) {
      continue;
    }

    const missing: MissingFigure[] = [];
    if (!hasVisibility) {
      missing.push('Visibility');
    }
    if (!hasSentiment) {
      missing.push(TONE_METRIC_LABEL);
    }

    brands.push({
      key: row.term,
      name: nameOf(row.term),
      facet: facetOf(row.term),
      visibility: hasVisibility ? row.visibility : undefined,
      sentiment: hasSentiment ? row.sentiment : undefined,
      answerCount: typeof row.answerCount === 'number' && Number.isFinite(row.answerCount) ? row.answerCount : undefined,
      missing,
    });
  }

  return brands;
}

/**
 * Which quadrant a coordinate falls in.
 *
 * Decided from the figures the reader actually sees — Visibility rounded to the whole percentage, and
 * Sentiment to two decimals — not from the raw values behind them. A Visibility of 0.4997 prints
 * "50%" and a Sentiment of 0.4997 prints "0.50", so both belong to the higher quadrant on their axis;
 * deciding from the raw figure would place them in the lower one and have the hover read "Visibility
 * 50 %, … Niche Players" — the figure and the quadrant contradicting each other in one sentence. Read
 * this way the label and the placement agree by construction, and a brand exactly on a midline reads
 * in the HIGHER quadrant on that axis — right for Visibility, top for Sentiment.
 */
export function quadrantOf(visibility: number, sentiment: number): BrandQuadrant {
  const right = displayedVisibility(visibility) >= QUADRANT_MIDPOINT;
  const top = displayedSentiment(sentiment) >= QUADRANT_MIDPOINT;

  if (top) {
    return right ? 'Leaders' : 'Niche Players';
  }
  return right ? 'Controversial' : 'Laggers';
}

/** Visibility rounded to the whole percentage the axis and hover show, back on the 0–1 scale. */
function displayedVisibility(visibility: number): number {
  return Math.round(clamp(visibility, 0, 1) * 100) / 100;
}

/** Sentiment rounded to the two decimals the hover shows. */
function displayedSentiment(sentiment: number): number {
  return Number(clamp(sentiment, 0, 1).toFixed(2));
}

/**
 * The candidate radii a crowded tile walks, as multiples of one tile width.
 *
 * The first ring is a full tile width, so two brands on the same coordinate always separate. The
 * last is one and a half, which is as far as a tile may drift before its position stops being the
 * measurement it stands for — past that the tile stays put and says so instead.
 */
const COLLISION_RING_RADII = [1, 1.25, 1.5] as const;
const COLLISION_RING_ANGLES = [0, 60, 120, 180, 240, 300] as const;

/**
 * Slack on the clearance test, so a candidate that is exactly one tile width away is not rejected by
 * the last bit of a cosine. Without it the placement of a crowded plot would turn on rounding.
 */
export const CLEARANCE_EPSILON = 1e-9;

function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(high, value));
}

/**
 * Where each plotted brand's tile goes inside a `width` × `height` plot rectangle.
 *
 * Placement runs in ascending tag-key order, never in the order rows arrived in, so the same set of
 * brands always lays out the same way — a reader who reloads the screen sees the picture they left.
 *
 * A tile whose centre lands within one tile width of one already placed walks a fixed sequence of
 * candidates — three rings, six angles each — and takes the first that clears every placed tile,
 * still fits inside the rectangle, and stays on the same side of the Visibility split as its true
 * position. That last clause is what keeps the drawing honest: the split is drawn, and a tile nudged
 * across it would sit under the corner name for a half of the plot its figures say it is not in.
 *
 * Sideways only, because Visibility is the only axis with a split drawn on it. Sentiment's window is
 * zoomed to the data and labelled by its bands, so there is no horizontal line for a tile to be on
 * the wrong side of — holding one would be enforcing a boundary nobody can see, and it costs the
 * walk half the room it has to separate a crowd with.
 *
 * When no candidate satisfies all three, the tile keeps its true coordinate and is flagged
 * `unseparated`: a tile drifted far enough to always clear would be stating a position the
 * measurement does not support, and the hover and the accessible label carry the true figures either
 * way.
 *
 * `sentimentDomain` is the window the Sentiment axis draws, which is zoomed to the data
 * (`sentimentPlotDomain`) rather than fixed. It is passed in rather than derived here so that the
 * axis and the tiles are placed by ONE window: a layout that computed its own would put a tile at a
 * height the scale beside it does not point at. It defaults to the whole scale, which is the
 * identity — a caller that passes no window lays out exactly what it always did.
 */
export function layoutBrands(
  brands: readonly LandscapeBrand[],
  {
    width,
    height,
    tileSize,
    sentimentDomain = FULL_SENTIMENT_DOMAIN,
  }: { width: number; height: number; tileSize: number; sentimentDomain?: SentimentDomain }
): PlacedBrand[] {
  const half = tileSize / 2;
  const minX = half;
  const maxX = Math.max(half, width - half);
  const minY = half;
  const maxY = Math.max(half, height - half);

  const ordered = brands.filter(isPlotted).sort((left, right) => (left.key < right.key ? -1 : left.key > right.key ? 1 : 0));

  const placedCenters: Array<{ x: number; y: number }> = [];
  const placed: PlacedBrand[] = [];

  const clears = (x: number, y: number): boolean =>
    placedCenters.every((center) => Math.hypot(center.x - x, center.y - y) >= tileSize - CLEARANCE_EPSILON) &&
    x >= minX - CLEARANCE_EPSILON &&
    x <= maxX + CLEARANCE_EPSILON &&
    y >= minY - CLEARANCE_EPSILON &&
    y <= maxY + CLEARANCE_EPSILON;

  // The Sentiment axis runs upwards, and pixels run downwards — and it runs over the drawn window
  // rather than over the whole scale, so this is the only place a sentiment becomes a height.
  const toY = (sentiment: number) => (1 - sentimentPosition(sentiment, sentimentDomain)) * height;

  // Where the one drawn split falls in pixels. A candidate has to stay on the same side of it as the
  // true position, so a tile is never drawn under the corner name for the half it is not in.
  const midX = QUADRANT_MIDPOINT * width;

  for (const brand of ordered) {
    const trueX = clamp(brand.visibility * width, minX, maxX);
    const trueY = clamp(toY(brand.sentiment), minY, maxY);
    const keepsSide = (x: number): boolean => x >= midX === trueX >= midX;

    let centerX = trueX;
    let centerY = trueY;
    let unseparated = false;

    if (!clears(trueX, trueY)) {
      const candidate = findFreeCandidate(trueX, trueY, tileSize, (x, y) => clears(x, y) && keepsSide(x));
      if (candidate) {
        centerX = candidate.x;
        centerY = candidate.y;
      } else {
        unseparated = true;
      }
    }

    placedCenters.push({ x: centerX, y: centerY });
    placed.push({
      brand,
      quadrant: quadrantOf(brand.visibility, brand.sentiment),
      x: centerX - half,
      y: centerY - half,
      unseparated,
    });
  }

  return placed;
}

function findFreeCandidate(
  trueX: number,
  trueY: number,
  tileSize: number,
  clears: (x: number, y: number) => boolean
): { x: number; y: number } | undefined {
  for (const ring of COLLISION_RING_RADII) {
    const radius = ring * tileSize;
    for (const angle of COLLISION_RING_ANGLES) {
      const radians = (angle * Math.PI) / 180;
      const x = trueX + radius * Math.cos(radians);
      const y = trueY + radius * Math.sin(radians);
      if (clears(x, y)) {
        return { x, y };
      }
    }
  }
  return undefined;
}

/** Visibility as the axis shows it: a whole percentage. The figure `quadrantOf` decides from. */
export function formatVisibility(visibility: number): string {
  return `${Math.round(displayedVisibility(visibility) * 100)}%`;
}

/** Sentiment as the axis shows it: two decimals on a 0–1 scale. The figure `quadrantOf` decides from. */
export function formatSentiment(sentiment: number): string {
  return displayedSentiment(sentiment).toFixed(2);
}

/** What a greyed brand's chip says on hover — one notice per figure nobody has measured. */
export function missingFigureNotice(brand: LandscapeBrand): string {
  return brand.missing.map((figure) => `No ${figure} measured yet`).join(' · ');
}

/**
 * The tile's hover text, and the chip's.
 *
 * The answer count is named as what it is — the answers the brand was seen in — and never as the n
 * behind either figure. Visibility and Sentiment are means over different sets of rows and the wire
 * carries no denominator for either, so "70 % of 148" would be an arithmetic claim nothing supports.
 * With no answer count the hover names no count at all rather than reaching for the prompt count.
 */
export function hoverTextFor(brand: LandscapeBrand): string {
  if (!isPlotted(brand)) {
    return missingFigureNotice(brand);
  }

  const figures = `Visibility ${formatVisibility(brand.visibility)}, ${TONE_METRIC_LABEL} ${formatSentiment(brand.sentiment)}`;
  const answers = answersSeenIn(brand.answerCount);

  return answers ? `${brand.name} — ${figures}, seen in ${answers}` : `${brand.name} — ${figures}`;
}

/**
 * The answers a brand was seen in, or nothing at all.
 *
 * A count of zero is not a count worth naming: a brand that carries a Visibility figure was seen
 * somewhere, so "seen in 0 answers" contradicts the figure beside it rather than qualifying it.
 */
function answersSeenIn(answerCount?: number): string | undefined {
  if (!answerCount) {
    return undefined;
  }

  return answerCount === 1 ? '1 answer' : `${answerCount} answers`;
}

/** How many brands a "Not plotted" line names before it counts the rest. */
export const NOT_PLOTTED_NAMED_LIMIT = 3;

/**
 * The lines under the plot, one per figure nobody has measured for somebody.
 *
 * A brand missing both figures is named on both lines: each line answers one question — "who is
 * missing from the plot because of this figure" — and leaving it off one of them would make that
 * line lie about its own subject.
 */
export function notPlottedLines(brands: readonly LandscapeBrand[]): string[] {
  const lines: string[] = [];

  // Ordered by tag key before any name is picked, the way `layoutBrands` and `buildTileColors` order
  // theirs, so the same set of brands reads the same regardless of the order the rows arrived in.
  const ordered = [...brands].sort((left, right) => (left.key < right.key ? -1 : left.key > right.key ? 1 : 0));

  for (const figure of ['Visibility', TONE_METRIC_LABEL] as const) {
    const named = ordered.filter((brand) => brand.missing.includes(figure)).map((brand) => brand.name);
    if (!named.length) {
      continue;
    }

    const shown = named.slice(0, NOT_PLOTTED_NAMED_LIMIT).join(', ');
    const rest = named.length - NOT_PLOTTED_NAMED_LIMIT;
    lines.push(`Not plotted, no ${figure} measured yet: ${shown}${rest > 0 ? ` and ${rest} more` : ''}.`);
  }

  return lines;
}

/** The tile colour for a tag no palette claims a relationship with. */
export const NEUTRAL_TILE_COLOR = '#64748B';

const COMPETITOR_FACETS: readonly TaxonomyFacet[] = [
  TaxonomyFacet.Competitor,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.CompetitorProgram,
];

/**
 * A colour per brand, derived from the tag's facet rather than borrowed from the entity palette.
 *
 * The entity palette builds its colours from entity objects this screen never loads, and it
 * deliberately withholds a colour from anything that is neither own nor competitor. This card needs
 * a tile behind every set of initials, so it derives one from what the row does carry — its facet —
 * using the two colour sources the screen already reads.
 *
 * Colour carries no identity here and is never the only thing telling two tiles apart: every tile
 * shows the brand's logo or its initials, its name sits beside it in the strip, and the hover and
 * the accessible label name it outright. The tile colour says which KIND of tag this is.
 *
 * Competitors take successive palette entries in ascending tag-key order, so the same set of brands
 * always produces the same colours.
 *
 * `hasLogo` names the brands whose tile draws a logo instead of a coloured square, and it changes
 * only the ORDER competitors are dealt from the palette: those brands are served last. The palette
 * is seven entries and wraps, so a competitor showing a logo that took an early entry would push the
 * colours of two brands that have nothing else to tell them apart onto the same hue. Served last,
 * every competitor still gets a competitor colour — which is what its tile falls back to while the
 * logo is still loading, and for good if the logo turns out not to exist.
 */
export function buildTileColors(
  brands: readonly LandscapeBrand[],
  { primaryColor, palette, hasLogo }: { primaryColor: string; palette: readonly string[]; hasLogo?: (key: string) => boolean }
): ReadonlyMap<string, string> {
  const colors = new Map<string, string>();
  const byName = new Map<string, string>();
  let nextPaletteIndex = 0;

  const ordered = [...brands].sort((left, right) => (left.key < right.key ? -1 : left.key > right.key ? 1 : 0));
  const isCompetitor = (brand: LandscapeBrand) => !!brand.facet && COMPETITOR_FACETS.includes(brand.facet);
  const showsLogo = (brand: LandscapeBrand) => !!hasLogo?.(brand.key);

  // Keyed by name so the same competitor keeps one colour wherever it appears.
  for (const brand of [
    ...ordered.filter((entry) => isCompetitor(entry) && !showsLogo(entry)),
    ...ordered.filter((entry) => isCompetitor(entry) && showsLogo(entry)),
  ]) {
    if (byName.has(brand.name)) {
      continue;
    }
    byName.set(brand.name, palette.length ? palette[nextPaletteIndex++ % palette.length] : NEUTRAL_TILE_COLOR);
  }

  for (const brand of ordered) {
    if (brand.facet === TaxonomyFacet.Brand) {
      colors.set(brand.key, primaryColor);
      continue;
    }

    colors.set(brand.key, (isCompetitor(brand) ? byName.get(brand.name) : undefined) ?? NEUTRAL_TILE_COLOR);
  }

  return colors;
}

/**
 * The ground a tile puts under a logo.
 *
 * Logos are drawn for paper: most arrive as dark ink on transparency, and one laid straight over the
 * plot would be a mark whose legibility depends on what the plot happens to be painted. White is
 * also what separates the tile from the plot behind it, which is the job the facet colour does for
 * every other tile.
 */
export const LOGO_TILE_BACKGROUND = '#FFFFFF';

/** The two inks a tile may carry. */
export const TILE_INK_LIGHT = '#FFFFFF';
export const TILE_INK_DARK = '#0F172A';

/**
 * The red, green and blue of a colour, or undefined for a notation this cannot read.
 *
 * Both notations the theme can hand this card are covered: the stylesheet's own `#rrggbb`, and the
 * `rgb(...)` a host theme's custom property resolves to — the same pair `withAlpha` reads in
 * `governanceThemeColors.ts`. Anything else has no ink this can choose honestly.
 */
function readChannels(color: string): [number, number, number] | undefined {
  const value = color.trim();

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value);
  if (hex) {
    const digits = hex[1].length === 3 ? hex[1].replace(/./g, (char) => char + char) : hex[1];
    const parsed = Number.parseInt(digits, 16);
    return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
  }

  const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(value);
  if (rgb) {
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  }

  return undefined;
}

/** Relative luminance, as WCAG defines it. Undefined for a colour this cannot read. */
function relativeLuminance(color: string): number | undefined {
  const channels = readChannels(color)?.map((channel) => {
    const ratio = Math.max(0, Math.min(255, channel)) / 255;
    return ratio <= 0.04045 ? ratio / 12.92 : Math.pow((ratio + 0.055) / 1.055, 2.4);
  });

  return channels ? 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2] : undefined;
}

function contrastRatio(first: number, second: number): number {
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/**
 * Ink that stays legible on a tile of the given colour.
 *
 * The two inks are COMPARED by contrast rather than split at a guessed luminance: the crossover
 * between them sits near 0.20, not at the midpoint, and the whole comparison palette lives between
 * those two values — a midpoint test would hand every real tile the weaker of the two inks. The
 * initials on a tile are the brand's only identity wherever no logo is known, so this is the one
 * place on the card where colour has to carry text.
 */
export function tileInkFor(color: string): string {
  const luminance = relativeLuminance(color);
  if (luminance === undefined) {
    return TILE_INK_LIGHT;
  }

  const light = relativeLuminance(TILE_INK_LIGHT) ?? 1;
  const dark = relativeLuminance(TILE_INK_DARK) ?? 0;

  return contrastRatio(luminance, dark) > contrastRatio(luminance, light) ? TILE_INK_DARK : TILE_INK_LIGHT;
}

/** The letters a tile shows when no logo is known for the brand. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) {
    return '?';
  }

  // By code point, not by code unit: indexing a name that starts outside the basic plane would cut a
  // surrogate pair in half and draw a replacement glyph where a letter belongs.
  const lettersOf = (word: string) => Array.from(word);
  if (words.length === 1) {
    return lettersOf(words[0]).slice(0, 2).join('').toUpperCase();
  }

  return `${lettersOf(words[0])[0]}${lettersOf(words[1])[0]}`.toUpperCase();
}

/**
 * Everything the plot conveys, as one sentence per fact.
 *
 * The tiles are pictures at pixel coordinates: nothing about a brand's position is available to a
 * reader who is not looking at them, so the label repeats it all — the quadrant rules first, so the
 * figures that follow mean something, then every plotted brand with the figures, the answers and the
 * quadrant its tile shows.
 *
 * Read from the placement, because the one thing the picture can hide — a tile drawn under another
 * one — exists only once the tiles have positions.
 */
export function plotAccessibleLabel(placed: readonly PlacedBrand[], overlapping: ReadonlySet<string> = new Set()): string {
  const rules = BRAND_QUADRANTS.map((quadrant) => `${quadrant}: ${QUADRANT_RULES[quadrant]}`).join('. ');
  const heading = `Brand landscape: Visibility against ${TONE_METRIC_LABEL}. ${rules}.`;

  if (!placed.length) {
    return `${heading} No brands plotted.`;
  }

  const brands = placed.map((entry) => {
    const answers = answersSeenIn(entry.brand.answerCount);
    const seenIn = answers ? `, seen in ${answers}` : '';
    // A tile drawn under another one is the one thing a sighted reader can miss and this label
    // cannot, so it is named rather than left to the picture.
    const covered = overlapping.has(entry.brand.key) ? ', tile overlapping another' : '';
    return `${entry.brand.name}: Visibility ${formatVisibility(entry.brand.visibility)}, ${TONE_METRIC_LABEL} ${formatSentiment(
      entry.brand.sentiment
    )}${seenIn}, ${entry.quadrant}${covered}`;
  });

  return `${heading} ${brands.join('. ')}.`;
}

/**
 * Which of the drawn tiles sit under another one.
 *
 * Computed over what is actually on the plot rather than read off the placement record: a tile the
 * layout could not separate from a brand the reader has since switched off is alone on screen, and
 * telling them otherwise would be describing a picture that is not there.
 *
 * Separation is one measure across this whole card — a full tile width between centres — and this
 * reads it the same way the placement rule does. Tiles are squares, so a pair set a tile width apart
 * at an angle can still share a corner without being called a stack: calling those overlapping would
 * mark almost every tile the walk ever moved, and the mark would stop meaning anything on the one
 * case it exists for, a tile drawn directly on top of another.
 */
export function overlappingKeys(placed: readonly PlacedBrand[], tileSize: number): Set<string> {
  const overlapping = new Set<string>();

  for (let left = 0; left < placed.length; left++) {
    for (let right = left + 1; right < placed.length; right++) {
      if (Math.hypot(placed[left].x - placed[right].x, placed[left].y - placed[right].y) < tileSize - CLEARANCE_EPSILON) {
        overlapping.add(placed[left].brand.key);
        overlapping.add(placed[right].brand.key);
      }
    }
  }

  return overlapping;
}

/**
 * The chips, in the order the strip shows them: most visible brand first.
 *
 * The strip is the first thing on the card and the only list of brands it has, so it answers the
 * question the card is opened with — who is seen most — before the reader has looked at the plot.
 * Sorted here rather than at the point of rendering because the rows arrive in the Gaps table's own
 * order, which is a different question's answer.
 *
 * A brand nobody has measured Visibility for sorts last, together with the ones missing Sentiment:
 * those are the greyed, unplottable chips, and a brand with no figure has no place among the ranked
 * ones. Ties break by name and then by tag key, so equal visibility is still one fixed order rather
 * than whatever the rows happened to carry — the same guarantee the layout and the colours give.
 *
 * Ordering the strip does NOT touch the plot: `layoutBrands` and `buildTileColors` sort by tag key
 * themselves, so a tile keeps its position and its colour however the chips above it are arranged.
 */
export function chipOrder(brands: readonly LandscapeBrand[]): LandscapeBrand[] {
  return [...brands].sort((left, right) => {
    // An unmeasured brand sorts below every measured one, including a measured zero.
    const byVisibility = (right.visibility ?? -1) - (left.visibility ?? -1);
    if (byVisibility) {
      return byVisibility;
    }

    if (left.name !== right.name) {
      return left.name < right.name ? -1 : 1;
    }

    return left.key < right.key ? -1 : left.key > right.key ? 1 : 0;
  });
}

/** Whether a brand's tile is on the plot: plottable, and not switched off. */
export function isPressed(brand: LandscapeBrand, hidden: ReadonlySet<string>): boolean {
  return isPlotted(brand) && !hidden.has(brand.key);
}

/**
 * The brands the reader has switched off, carried across a change of rows.
 *
 * The state is the switched-OFF set rather than the pressed set, because those two are only the same
 * until the rows change: a pressed set cannot tell a brand the reader deliberately switched off from
 * one that has just arrived, and would press both. Held this way, a brand that arrives is pressed
 * because nothing switched it off, a brand that leaves drops out, and a brand the reader switched off
 * stays off.
 */
export function reconcileSelection(previous: ReadonlySet<string>, brands: readonly LandscapeBrand[]): Set<string> {
  const present = new Set(brands.filter(isPlotted).map((brand) => brand.key));
  return new Set([...previous].filter((key) => present.has(key)));
}

/** The card header's second line: which tags are in view, and where to change them. */
export function tagsInViewLine(names: readonly string[]): string {
  if (!names.length) {
    // Still pointing at the buttons: they render from the configured facets, not from the selection,
    // so a reader who has switched every tag off is looking straight at the way back.
    return 'No tags in view — turn one on with the buttons on Gaps below.';
  }

  const joined = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Showing ${joined} — change these with the buttons on Gaps below.`;
}

/**
 * Shown in place of the empty line when the Gaps figures could not be refreshed.
 *
 * A failed refresh clears the rows before it shows this, so it is never mistaken for a subject nobody
 * has measured: the rows a failed refresh would otherwise strand belong to the selection that last
 * succeeded, not to the one in front of the reader now. The Gaps table and the Brand landscape card
 * read the same rows and say the same thing when they are gone.
 */
export const GAPS_REFRESH_FAILED_MESSAGE = 'These figures could not be refreshed. Check the connection and try again.';
